// Cost rollup — 다단계 BOM × 표준 단가 → 표준 원가 + 작업지시 estimated vs actual
import { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Card } from '../components/Card'
import { Button } from '../components/Button'
import { Counter } from '../components/Counter'
import { Skeleton, SkeletonRows } from '../components/Skeleton'
import { api } from '../lib/api'
import { useToast } from '../components/Toast'
import { useGlobalReveal } from '../hooks/useReveal'
import { useT } from '../lib/i18n'

interface BomHead { bom_id: string; product_item_code: string; version: string }

interface ExplodedRow {
  component_item_code: string
  parent_item_code: string
  required_qty: number
  uom: string
  scrap_percent: number
  level: number
  is_subassembly: boolean
  path: string[]
}

interface CostBreakdownRow {
  item_code: string
  required_qty: number
  unit_cost: number
  extended_cost: number
  level: number
  is_subassembly: boolean
}

interface BomCostResp {
  top_item: string
  produce_qty: number
  exploded: ExplodedRow[]
  breakdown: {
    total_cost: number
    by_item: CostBreakdownRow[]
    missing_costs: string[]
  }
  unit_cost: number
  top_standard_cost: number | null
  margin: number | null
}

interface WoCost {
  work_order_id: string
  line_id: string
  equipment_id: string
  status: string
  start_date: string
  end_date: string
  priority: number
  item_code: string
  po_quantity: number
  estimated_cost: number | null
  actual_cost: number | null
  variance_percent: number | null
}

const fmt = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : `₩${Math.round(n).toLocaleString('ko-KR')}`

export function CostPage() {
  useGlobalReveal()
  const toast = useToast()
  const { t } = useT()
  const [boms, setBoms] = useState<BomHead[] | null>(null)
  const [topItem, setTopItem] = useState<string>('')
  const [qty, setQty] = useState<string>('100')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<BomCostResp | null>(null)
  const [wo, setWo] = useState<WoCost[] | null>(null)

  useEffect(() => {
    api.get<BomHead[]>('/boms')
      .then((d) => {
        setBoms(d)
        if (d[0]) setTopItem(d[0].product_item_code)
      })
      .catch((e) => { setBoms([]); toast.push('error', e?.message ?? '조회 실패') })
    api.get<WoCost[]>('/cost/work-orders')
      .then(setWo)
      .catch((e) => { setWo([]); toast.push('error', e?.message ?? '작업지시 조회 실패') })
  }, [toast])

  const run = async () => {
    if (!topItem) return
    const q = Number(qty)
    if (!Number.isFinite(q) || q <= 0) { toast.push('error', '수량은 양의 숫자'); return }
    setBusy(true); setResult(null)
    try {
      const r = await api.get<BomCostResp>(
        `/cost/bom?item=${encodeURIComponent(topItem)}&qty=${q}`,
      )
      setResult(r)
    } catch (e: any) {
      toast.push('error', e?.message ?? '계산 실패')
    } finally { setBusy(false) }
  }

  // 품번 unique (BOM이 여러 버전이면 첫 것)
  const itemOptions = (boms ?? []).reduce((acc, b) => {
    if (!acc.find((x) => x.code === b.product_item_code)) {
      acc.push({ code: b.product_item_code })
    }
    return acc
  }, [] as Array<{ code: string }>)

  return (
    <main id="main" className="container stack" style={{ paddingBlock: '2rem' }}>
      <h1 className="page-title reveal">
        <FontAwesomeIcon icon="money-bill-trend-up" /> {t('cost.title')}
      </h1>
      <p className="muted reveal">{t('cost.intro')}</p>

      {/* 컨트롤 */}
      <Card glass tilt={false} spotlight={false} className="reveal" as="section">
        <div className="row" style={{ alignItems: 'end', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div className="field" style={{ flex: '1 1 220px', marginBottom: 0 }}>
            <label htmlFor="top-item">최상위 품번</label>
            <select id="top-item" value={topItem} onChange={(e) => setTopItem(e.target.value)} disabled={!boms}>
              {!boms && <option>로드 중…</option>}
              {itemOptions.map((i) => (
                <option key={i.code} value={i.code}>{i.code}</option>
              ))}
            </select>
          </div>
          <div className="field" style={{ flex: '0 1 140px', marginBottom: 0 }}>
            <label htmlFor="qty">생산 수량</label>
            <input id="qty" type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} />
          </div>
          <Button variant="accent" magnetic={false} disabled={busy || !topItem} onClick={run}>
            <FontAwesomeIcon icon={busy ? 'circle-notch' : 'calculator'} spin={busy} />
            <span>원가 계산</span>
          </Button>
        </div>
      </Card>

      {/* KPI */}
      {result && (
        <section className="reveal" style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
          <Card glass tilt={false} spotlight={false}>
            <span className="badge"><FontAwesomeIcon icon="boxes-stacked" /> 생산 수량</span>
            <p className="kpi-num">
              <Counter value={result.produce_qty} /> EA
            </p>
          </Card>
          <Card glass tilt={false} spotlight={false}>
            <span className="badge info"><FontAwesomeIcon icon="money-bill-trend-up" /> 표준 원가 (총액)</span>
            <p className="mega-num" style={{ margin: '0.4rem 0 0' }}>
              ₩<Counter value={Math.round(result.breakdown.total_cost)} />
            </p>
          </Card>
          <Card glass tilt={false} spotlight={false}>
            <span className="badge"><FontAwesomeIcon icon="calculator" /> 단위 원가</span>
            <p className="kpi-num">
              {fmt(result.unit_cost)}
            </p>
          </Card>
          {result.top_standard_cost !== null && (
            <Card glass tilt={false} spotlight={false}>
              <span className={`badge ${result.margin !== null && result.margin >= 0 ? 'success' : 'danger'}`}>
                <FontAwesomeIcon icon="scale-balanced" /> 표준단가 - 실원가
              </span>
              <p className="kpi-num">
                {fmt(result.margin)}
              </p>
              <p className="muted" style={{ margin: '0.2rem 0 0', fontSize: 'var(--fs-sm)' }}>
                기준 단가 {fmt(result.top_standard_cost)}
              </p>
            </Card>
          )}
        </section>
      )}

      {/* missing costs */}
      {result && result.breakdown.missing_costs.length > 0 && (
        <Card glass tilt={false} spotlight={false} className="reveal" as="section">
          <span className="badge warn">
            <FontAwesomeIcon icon="triangle-exclamation" /> 단가 미등록 자재 {result.breakdown.missing_costs.length}건
          </span>
          <p className="muted" style={{ marginTop: '0.4rem', fontSize: 'var(--fs-sm)' }}>
            아래 자재는 item_master.standard_cost가 비어 있어 원가에 포함되지 않았습니다.
          </p>
          <div className="row" style={{ flexWrap: 'wrap', gap: '0.3rem', marginTop: '0.4rem' }}>
            {result.breakdown.missing_costs.map((m) => (
              <span key={m} className="badge mono">{m}</span>
            ))}
          </div>
        </Card>
      )}

      {/* 다단계 breakdown 트리 */}
      {result && (
        <Card glass tilt={false} spotlight={false} className="reveal" as="section" aria-labelledby="tree-h">
          <h2 id="tree-h" style={{ fontSize: 'var(--fs-lg)' }}>다단계 BOM 분해 ({result.breakdown.by_item.length}건)</h2>
          <div className="table-wrap" style={{ '--table-min-w': '780px' } as React.CSSProperties}>
            <table className="table" aria-label="다단계 BOM cost breakdown">
              <thead>
                <tr>
                  <th>구성</th>
                  <th>필요량</th>
                  <th>단가</th>
                  <th>금액</th>
                  <th>구분</th>
                </tr>
              </thead>
              <tbody>
                {result.breakdown.by_item.map((b, i) => (
                  <tr key={i} style={b.is_subassembly ? { background: 'var(--color-bg-soft)' } : undefined}>
                    <td>
                      <span style={{ display: 'inline-block', width: (b.level - 1) * 20, opacity: 0.4 }}>
                        {b.level > 1 ? '└─ ' : ''}
                      </span>
                      <span className="mono">{b.item_code}</span>
                    </td>
                    <td className="mono">{b.required_qty.toFixed(3)}</td>
                    <td className="mono muted">{b.unit_cost ? fmt(b.unit_cost) : '—'}</td>
                    <td className="mono">
                      {b.is_subassembly ? <span className="muted">(자식 자재 합으로 분해)</span> : <strong>{fmt(b.extended_cost)}</strong>}
                    </td>
                    <td>
                      <span className={`badge ${b.is_subassembly ? 'info' : ''}`}>
                        {b.is_subassembly ? 'sub-assy' : `leaf · L${b.level}`}
                      </span>
                    </td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={3} style={{ textAlign: 'right' }}><strong>합계</strong></td>
                  <td className="mono"><strong>{fmt(result.breakdown.total_cost)}</strong></td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* 작업지시 원가 분석 */}
      <Card glass tilt={false} spotlight={false} className="reveal" as="section" aria-labelledby="wo-h">
        <h2 id="wo-h" style={{ fontSize: 'var(--fs-lg)' }}>작업지시 원가 — Estimated vs Actual</h2>
        {!wo ? <SkeletonRows rows={4} /> : wo.length === 0 ? (
          <p className="muted">데이터 없음</p>
        ) : (
          <div className="table-wrap" style={{ '--table-min-w': '880px' } as React.CSSProperties}>
            <table className="table" aria-label="작업지시 원가">
              <thead>
                <tr>
                  <th>작업지시</th><th>품번</th><th>수량</th><th>상태</th>
                  <th>표준 원가</th><th>실제 원가</th><th>차이</th>
                </tr>
              </thead>
              <tbody>
                {wo.map((w) => {
                  const v = w.variance_percent
                  const vc = v === null ? '' : v > 5 ? 'danger' : v < -5 ? 'info' : 'success'
                  return (
                    <tr key={w.work_order_id}>
                      <td className="mono">{w.work_order_id}</td>
                      <td>{w.item_code}</td>
                      <td className="mono">{w.po_quantity.toLocaleString('ko-KR')}</td>
                      <td><span className={`badge ${w.status === 'running' ? 'info' : w.status === 'done' ? 'success' : ''}`}>{w.status}</span></td>
                      <td className="mono">{fmt(w.estimated_cost)}</td>
                      <td className="mono">{fmt(w.actual_cost)}</td>
                      <td>
                        {v === null ? (
                          <span className="muted">—</span>
                        ) : (
                          <span className={`badge ${vc}`}>
                            {v > 0 ? '+' : ''}{v.toFixed(1)}%
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </main>
  )
}
